import { last } from 'lodash';
import { action, computed, observable } from 'mobx';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import {
    QuestionType,
    TResponseBlock,
    TResponsesBlockTypes
} from '~/types/builder.types';
import { IResponseBlock, IResponsesData } from '~/types/model.types';
import { sortBlocksByDisplayPrecedence } from '~/utils/block.utils';
import type { RootStore } from '~/stores/root.store';

export class ResponsesUiStore {
    rootStore: RootStore;

    @observable selectedFlowId: string;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action.bound
    selectFlow(id?: string) {
        this.selectedFlowId = id;
    }

    @action.bound
    reset() {
        this.selectFlow(null);
    }

    @computed
    get selectedFlow() {
        return this.rootStore.flowStore.flows[this.selectedFlowId];
    }

    @computed
    get selectedFlowRevision(): FlowRevision<RootStore> {
        if (!this.selectedFlow) {
            return null;
        }
        return this.selectedFlow.is_published
            ? this.selectedFlow.activeRevision
            : this.selectedFlow.draftRevision;
    }

    @computed
    get smafActions() {
        return this.selectedFlow.actions;
    }

    @computed
    get responsesData(): IResponsesData {
        const { selectedFlow, selectedFlowRevision } = this;

        if (!selectedFlowRevision) {
            return {};
        }

        const { pages } = selectedFlowRevision;
        const actionBlocks: TResponseBlock[] = [];
        let questionsTotal = 0;
        let questionsCompleted = 0;
        let requiredQuestionsTotal = 0;
        let requiredQuestionsCompleted = 0;
        let requiredAttachmentsTotal = 0;
        const transformedPages = pages.map(page => {
            let responseBlocks: IResponseBlock[] = [];
            const filteredPageBlocks = page.blocks
                .filter(
                    block =>
                        block.isQuestionBlock() || block.isResponsesActionBlock
                )
                .sort(sortBlocksByDisplayPrecedence);

            (filteredPageBlocks as TResponseBlock[]).forEach(block => {
                // count total + required questions

                if (block instanceof QuestionBlock) {
                    questionsTotal++;
                    if (block.question.is_required) {
                        requiredQuestionsTotal++;
                    }
                    if (block.connectedVariable?.value) {
                        questionsCompleted++;
                        if (block.question.is_required) {
                            requiredQuestionsCompleted++;
                            if (
                                block.question.question_type ===
                                QuestionType.Upload
                            ) {
                                requiredAttachmentsTotal +=
                                    block.connectedVariable.value.length;
                            }
                        }
                    }
                }

                // group action blocks
                if (block.isResponsesActionBlock) {
                    actionBlocks.push(block);
                }

                responseBlocks = this.addBlockToResponseBlocks(
                    block,
                    responseBlocks
                );
            });

            // return transformed
            return { ...page, responseBlocks };
        });

        const pagesViewed = transformedPages.filter(
            page => page.views.length
        ).length;

        return {
            actionBlocks,
            pages: transformedPages,
            // This isn't being used right now but it causes the @computed to trigger
            // correctly when variable values updates
            variables: selectedFlowRevision?.variables,
            questionsTotal,
            questionsCompleted,
            requiredQuestionsTotal,
            requiredQuestionsCompleted,
            requiredAttachmentsTotal,
            pagesViewed,
            isPublished: selectedFlow?.is_published,
            lastViewedAt: selectedFlow?.last_viewed_at,
            firstPublishedAt: selectedFlow?.first_published_at,
            publishedAt: selectedFlow?.published_at,
            status: selectedFlow?.status,
            submittedOn: selectedFlow?.submitted_on,
            submittedBy: selectedFlow?.submitted_by_user,
            canceledOn: selectedFlow?.canceled?.canceled_on
        };
    }

    private addBlockToResponseBlocks(
        block: TResponseBlock,
        responseBlocks: IResponseBlock[]
    ): IResponseBlock[] {
        if (block instanceof QuestionBlock) {
            const prevItem = last(responseBlocks);
            if (prevItem?.type === 'question_block') {
                (prevItem.content as QuestionBlock[]).push(block);
                return responseBlocks;
            }
            return [
                ...responseBlocks,
                { type: block.type as TResponsesBlockTypes, content: [block] }
            ];
        }

        return [
            ...responseBlocks,
            {
                type: block.type as TResponsesBlockTypes,
                content: block
            }
        ];
    }
}
